<script setup>
/**
 * 积分商品编辑弹窗。售价按美元填写，交给接口前换算成美分——运营看的是 $9.99，不是 999。
 * 到账积分和折合回合随输入实时算出来，定价时不用心算。
 */
import { computed, reactive, watch } from 'vue'
import { Button, FormField, Input, Modal, SegmentedControl } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { turnsCoveredBy } from '../../pages/billing/format.js'

const USD_PATTERN = /^\d+(\.\d{1,2})?$/

const props = defineProps({
  open: { type: Boolean, required: true },
  product: { type: Object, default: null },
  /** 有效回合的当前单价，折合回合用它算。 */
  turnPrice: { type: Number, required: true },
  saving: { type: Boolean, required: true },
  error: { type: String, required: true },
})
const emit = defineEmits(['update:open', 'submit'])
const { t } = useI18n()

const form = reactive({ title: '', paid_points: '', bonus_points: '', price: '', stripe_price_id: '', active: 'on' })

watch(() => props.open, (open) => {
  if (!open) return
  form.title = props.product.title
  form.paid_points = String(props.product.paid_points)
  form.bonus_points = String(props.product.bonus_points)
  form.price = (props.product.price_cents / 100).toFixed(2)
  form.stripe_price_id = props.product.stripe_price_id === null ? '' : props.product.stripe_price_id
  form.active = props.product.active ? 'on' : 'off'
})

const activeOptions = computed(() => [
  { value: 'on', label: t('visualGameAdmin.billing.products.activeState.on') },
  { value: 'off', label: t('visualGameAdmin.billing.products.activeState.off') },
])
const titleError = computed(() => (form.title.trim() === '' ? t('visualGameAdmin.billing.products.errors.name') : ''))
const paidPointsError = computed(() => (isWholeNumber(form.paid_points, 1) ? '' : t('visualGameAdmin.billing.products.errors.paidPoints')))
const bonusPointsError = computed(() => (isWholeNumber(form.bonus_points, 0) ? '' : t('visualGameAdmin.billing.products.errors.bonusPoints')))
const priceError = computed(() => (
  USD_PATTERN.test(form.price.trim()) && priceCents() >= 1 ? '' : t('visualGameAdmin.billing.products.errors.price')
))
const valid = computed(() => [titleError, paidPointsError, bonusPointsError, priceError].every(field => field.value === ''))
const totalPoints = computed(() => Number(form.paid_points) + Number(form.bonus_points))
const turns = computed(() => turnsCoveredBy(totalPoints.value, props.turnPrice))

function isWholeNumber(text, min) {
  const value = Number(text)
  return text !== '' && Number.isInteger(value) && value >= min
}

function priceCents() {
  return Math.round(Number(form.price.trim()) * 100)
}

function submit() {
  if (!valid.value) return
  const stripePriceId = form.stripe_price_id.trim()
  emit('submit', {
    title: form.title.trim(),
    paid_points: Number(form.paid_points),
    bonus_points: Number(form.bonus_points),
    price_cents: priceCents(),
    currency: 'usd',
    stripe_price_id: stripePriceId === '' ? null : stripePriceId,
    active: form.active === 'on',
  })
}
</script>

<template>
  <Modal
    :open="open"
    :title="product === null ? '' : t('visualGameAdmin.billing.products.form.title', { name: product.title })"
    width="520px"
    @update:open="emit('update:open', $event)"
  >
    <form v-if="product !== null" class="vga-form" @submit.prevent="submit">
      <FormField :label="t('visualGameAdmin.billing.products.form.name')" :error="titleError" required>
        <Input v-model="form.title" />
      </FormField>
      <div class="vga-form__row">
        <FormField :label="t('visualGameAdmin.billing.products.form.paidPoints')" :error="paidPointsError" required>
          <Input v-model="form.paid_points" type="number" />
        </FormField>
        <FormField :label="t('visualGameAdmin.billing.products.form.bonusPoints')" :error="bonusPointsError" required>
          <Input v-model="form.bonus_points" type="number" />
        </FormField>
      </div>
      <dl class="product-derived">
        <div>
          <dt>{{ t('visualGameAdmin.billing.products.form.totalPoints') }}</dt>
          <dd>{{ valid ? totalPoints.toLocaleString('en-US') : '—' }}</dd>
        </div>
        <div>
          <dt>{{ t('visualGameAdmin.billing.products.form.turns') }}</dt>
          <dd>{{ valid ? t('visualGameAdmin.billing.turnsCovered', { n: turns }) : '—' }}</dd>
        </div>
      </dl>
      <FormField :label="t('visualGameAdmin.billing.products.form.price')" :hint="t('visualGameAdmin.billing.products.form.priceHint')" :error="priceError" required>
        <Input v-model="form.price" placeholder="9.99" />
      </FormField>
      <FormField :label="t('visualGameAdmin.billing.products.form.stripePrice')" :hint="t('visualGameAdmin.billing.products.form.stripePriceHint')">
        <Input v-model="form.stripe_price_id" placeholder="price_…" />
      </FormField>
      <FormField :label="t('visualGameAdmin.billing.products.form.active')">
        <SegmentedControl v-model="form.active" :options="activeOptions" />
      </FormField>
      <p v-if="error" class="vga-error vga-form__error" role="alert">{{ error }}</p>
    </form>
    <template #footer>
      <Button variant="ghost" :disabled="saving" @click="emit('update:open', false)">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button :loading="saving" :disabled="!valid" @click="submit">{{ t('visualGameAdmin.common.save') }}</Button>
    </template>
  </Modal>
</template>

<style scoped>
.product-derived {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--drama-space-md);
  margin: 0;
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.product-derived dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.product-derived dd {
  margin: var(--drama-space-xs) 0 0;
  font-family: var(--vgs-font-mono);
  font-size: var(--drama-font-size-lg);
  font-weight: var(--drama-font-weight-medium);
}
</style>
